import { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Switch,
  Segmented,
  Button,
  Space,
  message as antdMessage,
} from "antd";
import { TranslationOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  BannerSeverity,
  CreateBannerRequestBody,
  IBannerObject,
  LocalizedBannerText,
} from "../../../../interfaces/banner.interface";
import { Language } from "../../../../interfaces/language.enum";
import { useAppDispatch } from "../../../../app/hooks";
import {
  createBannerThunk,
  translateBannerThunk,
  updateBannerThunk,
} from "../../bannersSlice";
import { LOCALE_LABELS, LOCALE_ORDER } from "../../bannerLocales";

interface BannerEditorModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  banner?: IBannerObject | null;
}

interface LocaleDraft {
  title?: string;
  message: string;
  actionText?: string;
}

type LocaleDraftField = keyof LocaleDraft;

const EMPTY_DRAFT: LocaleDraft = { message: "" };

export default function BannerEditorModal({
  open,
  onClose,
  onSaved,
  banner,
}: BannerEditorModalProps) {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [sourceLocale, setSourceLocale] = useState<Language>(Language.English);
  const [activeLocale, setActiveLocale] = useState<Language>(Language.English);
  const [drafts, setDrafts] = useState<Partial<Record<Language, LocaleDraft>>>(
    {}
  );

  useEffect(() => {
    if (!open) return;
    if (banner) {
      const nextDrafts: Partial<Record<Language, LocaleDraft>> = {};
      LOCALE_ORDER.forEach((loc) => {
        const title = banner.title?.[loc];
        const messageText = banner.message?.[loc];
        const actionText = banner.actionText?.[loc];
        if (title || messageText || actionText) {
          nextDrafts[loc] = { title, message: messageText ?? "", actionText };
        }
      });
      setDrafts(nextDrafts);
      setSourceLocale(banner.sourceLocale);
      setActiveLocale(banner.sourceLocale);
      form.setFieldsValue({
        severity: banner.severity,
        actionUrl: banner.actionUrl,
        dismissible: banner.dismissible,
        isActive: banner.isActive,
        startAt: banner.startAt ? dayjs(banner.startAt) : undefined,
        endAt: banner.endAt ? dayjs(banner.endAt) : undefined,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        severity: BannerSeverity.Info,
        dismissible: true,
        isActive: true,
      });
      setDrafts({});
      setSourceLocale(Language.English);
      setActiveLocale(Language.English);
    }
  }, [banner, open, form]);

  const activeDraft = drafts[activeLocale] ?? EMPTY_DRAFT;

  const patchActiveDraft = (partial: Partial<LocaleDraft>) =>
    setDrafts((prev) => ({
      ...prev,
      [activeLocale]: { ...(prev[activeLocale] ?? EMPTY_DRAFT), ...partial },
    }));

  const handleTranslate = async () => {
    const sourceDraft = drafts[sourceLocale];
    if (!sourceDraft?.message) {
      antdMessage.error("Enter a message in the source language first.");
      return;
    }
    setTranslating(true);
    try {
      const result = await dispatch(
        translateBannerThunk({
          sourceLocale,
          title: sourceDraft.title,
          message: sourceDraft.message,
          actionText: sourceDraft.actionText,
        })
      ).unwrap();
      setDrafts((prev) => {
        const next = { ...prev };
        LOCALE_ORDER.filter((loc) => loc !== sourceLocale).forEach((loc) => {
          next[loc] = {
            title: result.title?.[loc],
            message: result.message[loc] ?? "",
            actionText: result.actionText?.[loc],
          };
        });
        return next;
      });
      antdMessage.success("Translated into other languages.");
    } catch {
      antdMessage.error("Failed to translate.");
    } finally {
      setTranslating(false);
    }
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const sourceDraft = drafts[sourceLocale];
    if (!sourceDraft?.message) {
      antdMessage.error("Enter a message in the source language first.");
      return;
    }
    setSaving(true);
    try {
      const collect = (field: LocaleDraftField): LocalizedBannerText => {
        const map: LocalizedBannerText = {};
        LOCALE_ORDER.forEach((loc) => {
          const value = drafts[loc]?.[field];
          if (value) map[loc] = value;
        });
        return map;
      };

      const body: CreateBannerRequestBody = {
        ...values,
        title: collect("title"),
        message: collect("message"),
        actionText: collect("actionText"),
        sourceLocale,
        startAt: values.startAt ? values.startAt.toISOString() : undefined,
        endAt: values.endAt.toISOString(),
      };
      if (banner) {
        await dispatch(
          updateBannerThunk({ path: { bannerId: banner._id }, body })
        ).unwrap();
      } else {
        await dispatch(createBannerThunk(body)).unwrap();
      }
      onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={banner ? "Edit Banner" : "Create Banner"}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={banner ? "Save" : "Create"}
      confirmLoading={saving}
      okButtonProps={{ disabled: saving, loading: saving }}
      destroyOnClose
      width={640}
    >
      <Form layout="vertical" form={form} disabled={saving}>
        <div
          style={{
            overflowX: "auto",
            maxWidth: "100%",
            marginBottom: 8,
          }}
        >
          <Segmented
            value={activeLocale}
            onChange={(val) => setActiveLocale(val as Language)}
            options={LOCALE_ORDER.map((loc) => ({
              label: drafts[loc]?.message
                ? (LOCALE_LABELS[loc] ?? loc)
                : `${LOCALE_LABELS[loc] ?? loc} (empty)`,
              value: loc,
            }))}
          />
        </div>
        <Space style={{ marginBottom: 16 }} wrap>
          <Button
            icon={<TranslationOutlined />}
            loading={translating}
            disabled={!drafts[sourceLocale]?.message}
            onClick={handleTranslate}
          >
            Auto-translate
          </Button>
        </Space>

        <Form.Item label="Source language">
          <Select
            value={sourceLocale}
            onChange={(val) => setSourceLocale(val as Language)}
            options={LOCALE_ORDER.map((loc) => ({
              label: LOCALE_LABELS[loc] ?? loc,
              value: loc,
            }))}
          />
        </Form.Item>

        <Form.Item
          label={`Title (${LOCALE_LABELS[activeLocale] ?? activeLocale})`}
        >
          <Input
            placeholder="Optional short headline"
            value={activeDraft.title ?? ""}
            onChange={(e) => patchActiveDraft({ title: e.target.value })}
          />
        </Form.Item>
        <Form.Item
          label={`Message (${LOCALE_LABELS[activeLocale] ?? activeLocale})`}
          required
        >
          <Input.TextArea
            rows={3}
            placeholder="Banner body text"
            value={activeDraft.message}
            onChange={(e) => patchActiveDraft({ message: e.target.value })}
          />
        </Form.Item>
        <Form.Item
          label={`Link Text (${LOCALE_LABELS[activeLocale] ?? activeLocale})`}
        >
          <Input
            placeholder="e.g. View status page"
            value={activeDraft.actionText ?? ""}
            onChange={(e) => patchActiveDraft({ actionText: e.target.value })}
          />
        </Form.Item>

        <Form.Item
          label="Severity"
          name="severity"
          rules={[{ required: true, message: "Please select a severity" }]}
        >
          <Select
            options={[
              { label: "Info", value: BannerSeverity.Info },
              { label: "Warning", value: BannerSeverity.Warning },
              { label: "Error", value: BannerSeverity.Error },
            ]}
          />
        </Form.Item>
        <Form.Item label="Link URL" name="actionUrl">
          <Input placeholder="https://status.klearly.eu/..." />
        </Form.Item>
        <Form.Item label="Start Date" name="startAt">
          <DatePicker
            showTime
            style={{ width: "100%" }}
            format="MMM D, YYYY HH:mm"
            placeholder="Now, if left blank"
            onChange={() => {
              if (form.getFieldValue("endAt")) {
                form.validateFields(["endAt"]).catch(() => undefined);
              }
            }}
          />
        </Form.Item>
        <Form.Item
          label="End Date"
          name="endAt"
          dependencies={["startAt"]}
          rules={[
            { required: true, message: "Please select an end date" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const startAt = getFieldValue("startAt");
                if (!value || !startAt || value.isAfter(startAt)) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("End date must be after start date")
                );
              },
            }),
          ]}
        >
          <DatePicker
            showTime
            style={{ width: "100%" }}
            format="MMM D, YYYY HH:mm"
          />
        </Form.Item>
        <Form.Item
          label="Dismissible"
          name="dismissible"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item label="Active" name="isActive" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}
